import axios from 'axios';
import {BackendErrorResponse} from '../../errors/backend-error-response';

/**
 * Держать в синхроне с AiUserFacingError::TECHNICAL_MARKERS (PHP).
 */
const TECHNICAL_ERROR_MARKERS = [
  'OpenRouter',
  'Failed to call',
  'API request failed',
  'ripgrep',
  'Argument #',
  'APP_HTTP_PROXY',
  'OPENROUTER_',
  'RuntimeException',
  'TypeError',
  'stack trace',
  'SQLSTATE',
  'cURL error',
  'ignitionTrace',
];

/**
 * Скрывает технические тексты ошибок от toast/UI (SSE, fetch).
 * Добавлено: 2026-07-01.
 */
export function isTechnicalClientErrorMessage(raw: string): boolean {
  const msg = raw.trim();
  if (!msg) {
    return false;
  }
  if (msg.startsWith('<!') || msg.length > 500) {
    return true;
  }
  if (msg.startsWith('{') || msg.startsWith('[')) {
    return true;
  }
  if (/\b[A-Z]\w*::/.test(msg)) {
    return true;
  }
  for (const marker of TECHNICAL_ERROR_MARKERS) {
    if (msg.toLowerCase().includes(marker.toLowerCase())) {
      return true;
    }
  }
  if (/\b(var\/www|\.php|\/storage\/)\b/.test(msg)) {
    return true;
  }
  return false;
}

function sanitizeDisplayMessage(raw: string | undefined): string | undefined {
  if (!raw?.trim()) {
    return undefined;
  }
  if (isTechnicalClientErrorMessage(raw)) {
    return undefined;
  }
  return raw.trim();
}

/**
 * Достаёт человекочитаемый текст ошибки из ответа API или из проброшенного Error.
 * Изменено: 2026-04-10 — для fetch/React Query (например SSE AI-чат) сервер приходит в `Error.message`
 * или как JSON-тело в строке; раньше такие ошибки игнорировались и показывался только дефолтный toast.
 * Изменено: 2026-07-01 — фильтрация технических сообщений (OpenRouter и др.).
 */
function messageFromPlainError(err: Error, field?: string | null): string | undefined {
  const raw = err.message?.trim();
  if (!raw) {
    return undefined;
  }

  if (raw.startsWith('{')) {
    try {
      const data = JSON.parse(raw) as BackendErrorResponse;
      if (field != null && data.errors?.[field] != null) {
        const fieldMessage = data.errors[field];
        const resolved = Array.isArray(fieldMessage) ? fieldMessage[0] : fieldMessage;
        return sanitizeDisplayMessage(resolved);
      }
      return sanitizeDisplayMessage(data.message?.trim());
    } catch {
      // не JSON — ниже
    }
  }

  return sanitizeDisplayMessage(raw);
}

export function getAxiosErrorMessage(
  err: unknown,
  field?: string | null
): string | undefined {
  if (axios.isAxiosError(err) && err.response) {
    const response = err.response.data as BackendErrorResponse;

    if (field != null) {
      const fieldMessage = response.errors?.[field];
      const resolved = Array.isArray(fieldMessage)
        ? fieldMessage[0]
        : fieldMessage;
      return sanitizeDisplayMessage(resolved);
    }

    return sanitizeDisplayMessage(response?.message);
  }

  if (err instanceof Error) {
    return messageFromPlainError(err, field);
  }

  return undefined;
}
